import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

const SITE_URL = 'https://itaobuyspread.fit';
const FAVICON_URL = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg';

interface FAQItem {
  q: string;
  a: string;
}

interface ArticleLayoutProps {
  breadcrumbLabel: string;
  badges: { label: string; variant: 'primary' | 'accent' | 'secondary' }[];
  title: string;
  updateDate: string;
  readTime: string;
  children: ReactNode;
  sidebarChildren?: ReactNode;
  faqItems?: FAQItem[];
}

export default function ArticleLayout({
  breadcrumbLabel,
  badges,
  title,
  updateDate,
  readTime,
  children,
  sidebarChildren,
  faqItems,
}: ArticleLayoutProps) {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const badgeColors: Record<string, string> = {
    primary: 'bg-primary-100 text-primary-700',
    accent: 'bg-accent-100 text-accent-700',
    secondary: 'bg-secondary-100 text-secondary-700',
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20">
        <article className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-20 py-10 md:py-16">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8" aria-label="Breadcrumb">
            <a href="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</a>
            <i className="ri-arrow-right-s-line text-sm"></i>
            <span className="text-foreground-600">{breadcrumbLabel}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            <div className="lg:w-[65%]">
              <header className="mb-10">
                <div className="flex items-center gap-2 mb-4">
                  {badges.map((badge) => (
                    <span key={badge.label} className={`px-3 py-1 rounded-full text-xs font-medium ${badgeColors[badge.variant] || badgeColors.primary}`}>
                      {badge.label}
                    </span>
                  ))}
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                  {title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400">
                  <span>Aggiornato: {updateDate}</span>
                  <span>&middot;</span>
                  <span>Tempo di lettura: {readTime}</span>
                  <span>&middot;</span>
                  <span>Redazione Itaobuy</span>
                </div>
              </header>

              <div className="prose-custom space-y-7 text-foreground-700 leading-relaxed text-sm md:text-base">
                {children}
              </div>

              {faqItems && faqItems.length > 0 && (
                <>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-10 mb-6">
                    Domande Frequenti
                  </h2>
                  <div className="space-y-4">
                    {faqItems.map((faq, idx) => (
                      <div key={idx} className="p-5 rounded-2xl bg-background-100">
                        <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2">{faq.q}</h3>
                        <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="mt-10 p-6 rounded-2xl bg-primary-50 border border-primary-100">
                <p className="text-foreground-700 text-sm mb-3">
                  Visita il portale ufficiale per esplorare tutte le categorie e trovare i prodotti migliori per te.
                </p>
                <a
                  href="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span>Torna alla homepage</span>
                  <i className="ri-arrow-right-line text-base"></i>
                </a>
              </div>
            </div>

            <aside className="lg:w-[35%] lg:sticky lg:top-24 lg:self-start">
              <div className="space-y-5">
                <div className="p-6 rounded-2xl bg-background-100">
                  <h3 className="font-heading font-semibold text-foreground-900 mb-4">
                    Categorie correlate
                  </h3>
                  <div className="space-y-2">
                    {[
                      { name: 'Scarpe e Sneakers', url: 'https://kaspreadsheet.com/category-911-Shoes.html', icon: 'ri-footprint-line' },
                      { name: 'Felpe e Maglioni', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html', icon: 'ri-t-shirt-line' },
                      { name: 'Giacche e Cappotti', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html', icon: 'ri-honour-line' },
                      { name: 'Accessori Moda', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html', icon: 'ri-glasses-line' },
                      { name: 'Borse e Cinture', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html', icon: 'ri-handbag-line' },
                    ].map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-background-50 transition-colors cursor-pointer group"
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                          <i className={`${cat.icon} text-sm`}></i>
                        </div>
                        <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors">{cat.name}</span>
                        <i className="ri-arrow-right-up-line text-xs text-foreground-300 ml-auto"></i>
                      </a>
                    ))}
                  </div>
                </div>

                {sidebarChildren}

                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full py-3.5 rounded-full bg-foreground-900 text-background-50 font-semibold text-sm hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap"
                >
                  <span>Buy It Now</span>
                  <i className="ri-arrow-right-up-line text-base"></i>
                </a>

                <a
                  href="/"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-full border border-background-300 text-foreground-600 text-sm hover:border-primary-400 hover:text-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-line text-base"></i>
                  <span>Torna alla homepage</span>
                </a>
              </div>
            </aside>
          </div>
        </article>
      </main>

      <Footer />

      {faqItems && faqItems.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqItems.map((faq) => ({
                '@type': 'Question',
                name: faq.q,
                acceptedAnswer: { '@type': 'Answer', text: faq.a },
              })),
            }),
          }}
        />
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: title,
            datePublished: '2026-07-14',
            dateModified: '2026-07-14',
            author: { '@type': 'Person', name: 'Redazione Itaobuy' },
            publisher: {
              '@type': 'Organization',
              name: 'Itaobuy Spreadsheet Italia',
              logo: { '@type': 'ImageObject', url: FAVICON_URL },
            },
            url: `${SITE_URL}${pathname}`,
            inLanguage: 'it',
            isAccessibleForFree: true,
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: SITE_URL,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: breadcrumbLabel,
                item: `${SITE_URL}${pathname}`,
              },
            ],
          }),
        }}
      />
    </div>
  );
}